/* global moment */
import { cloneDeep } from 'lodash'
import { Api } from '@/core/api.js'
import {
    WORKSTATIONS,
    WORKSTATION_IDS,
    LAYOUT_TEMPLATES,
    normalizeLayouts,
    MIN_SLOTS_PER_PRODUCT,
    DEFAULT_HOLDING_TIME,
    EXAMPLE_HOLDING_MAX_UNITS,
    EQUIPMENT_STATUS,
    BLOCKED_REASONS,
    SLOT_STATES
} from '@/store/static/phuProduction'
import { workstationIdFromPhus } from '@/components/domain/phu-production/utils/productWorkstation'
import { resolveSlotState } from '@/components/domain/phu-production/utils/slotState'

const api = new Api()

const MINUTE_MS = 60 * 1000
const PLS_PRODUCT_TYPE = 6
const FORECAST_TYPE = 'pls-stock'
const DATE_FORMAT = 'YYYY-MM-DD'
const DATETIME_FORMAT = 'YYYY-MM-DD HH:mm:ss'
const CONFIGURATION_URL = 'pls/phu/configuration'
const DEMO_SALE_URL = 'pls/phu/sales/demo'

export const PHU_TABS = {
    COOKING: 'cooking',
    HOLDING: 'holding',
    STOCK: 'stock'
}

const getDefaultState = () => ({
    products: {},
    recommendations: [],
    cookingBatches: [],
    assignedBatchIds: [],
    stock: [],
    summary: {},
    phus: [],
    editingPhuIds: [],
    layouts: normalizeLayouts(LAYOUT_TEMPLATES),
    minSlotsPerProduct: MIN_SLOTS_PER_PRODUCT,
    workstationServerIds: {},
    equipmentCatalog: [
        { type: 'broiler', name: 'Broiler', max: 6 },
        { type: 'fryer', name: 'Fryer', max: 4 }
    ],
    fryerEquipment: [],
    broilerEquipment: [],
    fixedLayout: false,
    fifoSales: false,
    forecastReady: null,
    activeTab: PHU_TABS.COOKING,
    activeWorkstationId: null,
    configuration: false
})

const belongsToWorkstation = (workstationId) => (item) => !workstationId || item.workstation_id === workstationId

const toTimestamp = (date) => moment(date, DATETIME_FORMAT).valueOf()

const businessDate = () => moment().format(DATE_FORMAT)

const findSlot = (phus, slotId) => {
    for (const phu of phus) {
        const slot = phu.slots.find((item) => item.id === slotId)
        if (slot) {
            return slot
        }
    }
    return null
}

const optionList = (value) => (value && typeof value === 'object' ? value : {})

const clearSlot = (slot) => {
    slot.units = 0
    slot.batch_id = null
    slot.started_at = null
    slot.ready_at = null
    slot.expires_at = null
}

const slotSnapshot = (slot) => ({
    slotId: slot.id,
    units: slot.units,
    batch_id: slot.batch_id,
    started_at: slot.started_at,
    ready_at: slot.ready_at,
    expires_at: slot.expires_at
})

const planReadySlotWaste = (phus, productId, units, now) => {
    const eligible = []
    phus.forEach((phu) => {
        phu.slots.forEach((slot) => {
            if (String(slot.product_id) !== String(productId) || !(Number(slot.units) > 0)) {
                return
            }
            if (!slot.ready_at || slot.ready_at > now) {
                return
            }
            eligible.push(slot)
        })
    })
    eligible.sort((left, right) => left.ready_at - right.ready_at || Number(left.id) - Number(right.id))

    let remaining = units
    const plan = []
    eligible.forEach((slot) => {
        if (remaining <= 0) {
            return
        }
        const taken = Math.min(Number(slot.units), remaining)
        plan.push({ previous: slotSnapshot(slot), taken })
        remaining -= taken
    })

    return plan
}

const applyWastePlan = (commit, plan) => {
    plan.forEach((step) => {
        if (step.taken >= Number(step.previous.units)) {
            commit('clearSlot', step.previous.slotId)
        } else {
            commit('reduceSlotUnits', { slotId: step.previous.slotId, units: step.taken })
        }
    })
}

const visibleStockUnits = (stock, cookingBatches, productId, now) => {
    const row = (stock || []).find((item) => String(item.product_id) === String(productId))
    const visible = stockWithPendingProduction(row ? [row] : [], cookingBatches, now)

    return Math.max(0, Math.floor(Number(visible[0] && visible[0].units) || 0))
}

const applyProductWorkstations = (state) => {
    Object.values(state.products || {}).forEach((product) => {
        if (!product) {
            return
        }
        product.workstation_id = workstationIdFromPhus(state.phus, product.id) || WORKSTATION_IDS.FRYER
    })
}

const normalizeProducts = (rawProducts) => Object.values(rawProducts || {}).reduce((acc, product) => {
    if (!product || !product.id || product.type !== PLS_PRODUCT_TYPE) {
        return acc
    }
    acc[product.id] = {
        id: product.id,
        name: product.name,
        image: product.image || null,
        status: Boolean(product.status),
        cooking_time: Number(product.cooking_time) || 1,
        holding_time: Number(product.holding_time) || DEFAULT_HOLDING_TIME,
        holding_max_units: Number(product.holding_max_units) || EXAMPLE_HOLDING_MAX_UNITS,
        workstation_id: product.workstation_id || WORKSTATION_IDS.FRYER,
        containers: optionList(product.containers),
        packages: optionList(product.packages),
        drops: optionList(product.drops),
        units: optionList(product.units),
        active_container: product.active_container || null,
        active_package: product.active_package || null,
        active_drop: product.active_drop || null,
        active_unit: product.active_unit || null
    }
    return acc
}, {})

const normalizeRecommendations = (pls, products) => Object.entries(pls || {}).reduce((acc, [productId, hours]) => {
    if (!products[productId] || !hours) {
        return acc
    }
    Object.entries(hours).forEach(([recommendedDate, hourData]) => {
        const quantity = hourData && typeof hourData === 'object' ? hourData.quantity : hourData
        const batchId = hourData && typeof hourData === 'object' ? hourData.batch_id : null

        acc.push({
            id: batchId || `${productId}_${recommendedDate}`,
            batch_id: batchId,
            product_id: productId,
            units: Math.ceil(quantity || 0),
            recommended_date: recommendedDate,
            recommended_at: toTimestamp(recommendedDate),
            blocked: null
        })
    })
    return acc
}, [])

const normalizeCookingBatches = (cooking, products) => (cooking || []).reduce((acc, batch) => {
    const product = products[batch.reference_id]
    if (!product) {
        return acc
    }
    const endsAt = toTimestamp(batch.ready_date)
    acc.push({
        batch_id: batch.batch_id,
        product_id: batch.reference_id,
        units: Number(batch.quantity) || 0,
        started_at: endsAt - product.cooking_time * MINUTE_MS,
        ends_at: endsAt
    })
    return acc
}, [])

const movementValue = (movement, key) => (movement ? Number(movement[key]) || 0 : 0)

const storedUnits = (stored, productId) => {
    if (!stored || stored[productId] === undefined || stored[productId] === null || stored[productId] === '') {
        return 0
    }
    const units = Number(stored[productId])
    return Number.isFinite(units) ? units : 0
}

const normalizeStock = (stored, movements, products) => Object.values(products)
    .filter((product) => product.status)
    .map((product) => {
        const movement = movements && movements[product.id]
        return {
            product_id: product.id,
            units: storedUnits(stored, product.id),
            produced: movementValue(movement, 'produced'),
            sales: movementValue(movement, 'sales'),
            waste: movementValue(movement, 'waste')
        }
    })

const activeCookingBatches = (batches, now) => (batches || []).filter((batch) => batch.ends_at > now)

const pendingProduction = (cookingBatches, productId, now) => (cookingBatches || []).reduce((totals, batch) => {
    if (String(batch.product_id) !== String(productId)) {
        return totals
    }
    const units = Number(batch.units) || 0
    if (units <= 0) {
        return totals
    }
    totals.produced += units
    if (batch.ends_at <= now) {
        totals.ready += units
    }
    return totals
}, { produced: 0, ready: 0 })

const stockWithPendingProduction = (stock, cookingBatches, now) => (stock || []).map((item) => {
    const pending = pendingProduction(cookingBatches, item.product_id, now)
    if (pending.produced === 0) {
        return item
    }
    return {
        ...item,
        produced: item.produced + pending.produced,
        units: item.units + pending.ready
    }
})

const occupyEquipment = (equipment, workstationId, cookingBatches, now) => {
    const next = (equipment || []).map((item) => ({
        ...item,
        workstation_id: workstationId,
        status: item.status === EQUIPMENT_STATUS.UNAVAILABLE ? EQUIPMENT_STATUS.UNAVAILABLE : EQUIPMENT_STATUS.AVAILABLE,
        cooking: null
    }))
    const usable = next.filter((item) => item.status !== EQUIPMENT_STATUS.UNAVAILABLE)

    activeCookingBatches(cookingBatches, now).forEach((batch, index) => {
        if (!usable[index]) {
            return
        }
        usable[index].status = EQUIPMENT_STATUS.COOKING
        usable[index].cooking = batch
    })
    return next
}

const equipmentCanCook = (item, now) => item.status !== EQUIPMENT_STATUS.UNAVAILABLE && (!item.cooking || item.cooking.ends_at <= now)

const batchesForWorkstation = (state, workstationId) => state.cookingBatches.filter((batch) => {
    const product = state.products[batch.product_id]
    return product && product.workstation_id === workstationId
})

const findPhuOfSlot = (phus, slotId) => phus.find((phu) => phu.slots.some((slot) => slot.id === slotId)) || null

const RUNTIME_KEYS = ['units', 'batch_id', 'started_at', 'ready_at', 'expires_at']

const readRuntime = (slot) => RUNTIME_KEYS.reduce((runtime, key) => {
    runtime[key] = slot[key]
    return runtime
}, {})

const writeRuntime = (slot, runtime) => {
    RUNTIME_KEYS.forEach((key) => {
        slot[key] = runtime[key]
    })
}

const findPersistedSlot = (phus, visible) => {
    if (hasBatch(visible.batch_id)) {
        for (const phu of phus) {
            const slot = phu.slots.find((item) => hasBatch(item.batch_id) && String(item.batch_id) === String(visible.batch_id))
            if (slot) {
                return slot
            }
        }
        return null
    }
    return findSlot(phus, visible.id)
}

const hasBatch = (batchId) => batchId != null && batchId !== ''

const findSlotByBatch = (phus, batchId) => {
    for (const phu of phus) {
        const slot = phu.slots.find((item) => hasBatch(item.batch_id) && String(item.batch_id) === String(batchId) && Number(item.units) > 0)
        if (slot) {
            return slot
        }
    }
    return null
}

const resolveWasteSlot = (phus, visible) => {
    if (hasBatch(visible.batch_id)) {
        return findSlotByBatch(phus, visible.batch_id)
    }
    const clicked = findSlot(phus, visible.id)
    if (!clicked || Number(clicked.units) <= 0 || hasBatch(clicked.batch_id)) {
        return null
    }
    return clicked
}

const canCookInSlot = (slot, product) => Boolean(
    slot &&
    product &&
    String(slot.product_id) === String(product.id) &&
    !slot.batch_id &&
    !Number(slot.units)
)

const isFailedResponse = (response) => !response || response.status === false

const WORKSTATION_IDS_BY_TYPE = WORKSTATIONS.reduce((acc, workstation) => {
    acc[workstation.type] = workstation.id
    return acc
}, {})

const toTimestampOrNull = (date) => (date ? toTimestamp(date) : null)

const normalizeConfigSlot = (slot) => ({
    id: slot.id,
    position: slot.position,
    product_id: slot.reference_id || null,
    units: Number(slot.current_units) || 0,
    batch_id: slot.batch_id,
    started_at: toTimestampOrNull(slot.created_date),
    ready_at: toTimestampOrNull(slot.ready_date),
    expires_at: toTimestampOrNull(slot.expiration_date)
})

const normalizeConfigPhus = (workstations, layouts) => (workstations || []).reduce((acc, workstation) => {
    const workstationId = WORKSTATION_IDS_BY_TYPE[workstation.type]
    if (!workstationId) {
        return acc
    }
    workstation.phus.forEach((phu) => {
        const layout = (layouts || []).find((item) => Number(item.id) === Number(phu.layout_option_id))
        const size = layout || { rows: phu.rows, columns: phu.columns }
        acc.push({
            id: phu.id,
            workstation_id: workstationId,
            name: phu.name,
            layout_option_id: phu.layout_option_id,
            rows: size.rows,
            columns: size.columns,
            sort_order: phu.sort_order,
            fixed_layout: phu.fixed_layout,
            slots: phu.slots.map(normalizeConfigSlot)
        })
    })
    return acc
}, [])

const normalizeEquipmentCatalog = (catalog) => {
    const normalized = (Array.isArray(catalog) ? catalog : [])
        .filter((item) => item && (item.type === 'fryer' || item.type === 'broiler') && Number(item.max) > 0)
        .map((item) => ({ type: item.type, name: item.name || item.type, max: Number(item.max) }))

    return normalized.length ? normalized : getDefaultState().equipmentCatalog
}

const normalizeBroilers = (broilers) => (broilers || []).map((broiler) => ({
    id: `broiler-${broiler.id}`,
    number: broiler.id,
    workstation_id: WORKSTATION_IDS.BROILER,
    status: broiler.status,
    cooking: null,
    broiler: true
}))

const toBroilerPayload = (equipment) => equipment.map((item) => ({ id: item.number, status: item.status }))

const putConfiguration = (payload) => api.put(CONFIGURATION_URL, payload).then((response) => {
    if (isFailedResponse(response)) {
        throw new Error(response && response.msg ? response.msg : 'sync_error')
    }
    return response.data
})

let equipmentWrite = Promise.resolve()
const equipmentGeneration = { fryer: 0, broiler: 0 }
let holdingWrites = 0
let holdingGeneration = 0

const whileHoldingWrite = (work) => {
    holdingWrites += 1
    holdingGeneration += 1
    let result
    try {
        result = work()
    } catch (error) {
        holdingWrites = Math.max(0, holdingWrites - 1)
        holdingGeneration += 1
        throw error
    }
    return Promise.resolve(result).finally(() => {
        holdingWrites = Math.max(0, holdingWrites - 1)
        holdingGeneration += 1
    })
}

const isHoldingStale = (generation) => holdingWrites > 0 || generation !== holdingGeneration

const saveEquipment = (type, generation, readPayload) => {
    const result = equipmentWrite.then(() => putConfiguration(readPayload()), () => putConfiguration(readPayload()))
    equipmentWrite = result.then(
        () => undefined,
        () => undefined
    )
    return result.catch((error) => {
        if (generation !== equipmentGeneration[type]) {
            return undefined
        }
        throw error
    })
}

const state = getDefaultState()

const getters = {
    getWorkstations: (state) => state.equipmentCatalog.reduce((acc, item) => {
        const id = WORKSTATION_IDS_BY_TYPE[item.type]
        if (!id) {
            return acc
        }
        acc.push({
            id,
            type: item.type,
            name: item.name,
            server_id: state.workstationServerIds[item.type] || null
        })
        return acc
    }, []),
    getActiveWorkstationId: (state) => state.activeWorkstationId,
    getActiveTab: (state) => state.activeTab,
    isConfiguration: (state) => state.configuration,
    isFixedLayout: (state) => state.fixedLayout,
    isFifoSales: (state) => state.fifoSales,
    getLayoutTemplates: (state) => state.layouts,
    getMinSlotsPerProduct: (state) => state.minSlotsPerProduct,
    getEquipmentCatalog: (state) => state.equipmentCatalog,
    getFryerEquipment: (state) => state.fryerEquipment,
    getBroilerEquipment: (state) => state.broilerEquipment,
    isForecastMissing: (state) => state.forecastReady === false,
    getForecastDate: () => businessDate(),
    getSummary: (state) => state.summary,
    getProducts: (state) => state.products,
    getProduct: (state) => (productId) => state.products[productId] || null,
    getAllPhus: (state) => state.phus,
    getPhus: (state) => state.phus
        .filter(belongsToWorkstation(state.activeWorkstationId))
        .sort((a, b) => a.workstation_id - b.workstation_id || a.sort_order - b.sort_order),
    getPhusByWorkstation: (state) => (workstationId) => state.phus
        .filter((phu) => phu.workstation_id === workstationId)
        .sort((a, b) => a.sort_order - b.sort_order),
    getAllEquipment: (state) => (now = Date.now()) => [
        ...occupyEquipment(state.broilerEquipment, WORKSTATION_IDS.BROILER, batchesForWorkstation(state, WORKSTATION_IDS.BROILER), now),
        ...occupyEquipment(state.fryerEquipment, WORKSTATION_IDS.FRYER, batchesForWorkstation(state, WORKSTATION_IDS.FRYER), now)
    ],
    getEquipment: (state, getters) => (now = Date.now()) => getters.getAllEquipment(now).filter(belongsToWorkstation(state.activeWorkstationId)),
    getRecommendations: (state) => state.recommendations
        .filter((recommendation) => belongsToWorkstation(state.activeWorkstationId)(state.products[recommendation.product_id]))
        .sort((a, b) => a.recommended_at - b.recommended_at),
    getStock: (state) => (now = Date.now()) => stockWithPendingProduction(state.stock, state.cookingBatches, now)
        .filter((item) => belongsToWorkstation(state.activeWorkstationId)(state.products[item.product_id])),
    getAllStock: (state) => (now = Date.now()) => stockWithPendingProduction(state.stock, state.cookingBatches, now),
    hasAvailableEquipment: (state, getters) => (workstationId, now = Date.now()) => getters.getAllEquipment(now)
        .some((equipment) => equipment.workstation_id === workstationId && equipmentCanCook(equipment, now)),
    getTabCounters: (state, getters) => (now) => {
        const holding = getters.getPhus.reduce((total, phu) => total + phu.slots.filter((slot) => resolveSlotState(slot, now) === SLOT_STATES.EXPIRED).length, 0)

        return {
            [PHU_TABS.COOKING]: getters.getEquipment(now).filter((item) => item.status === EQUIPMENT_STATUS.COOKING).length,
            [PHU_TABS.HOLDING]: holding,
            [PHU_TABS.STOCK]: 0
        }
    }
}

const mutations = {
    setActiveTab(state, tab) {
        state.activeTab = tab
    },
    setActiveWorkstation(state, workstationId) {
        state.activeWorkstationId = workstationId
    },
    setConfiguration(state, value) {
        state.configuration = value
    },
    setProducts(state, products) {
        state.products = products
        applyProductWorkstations(state)
    },
    setPhus(state, phus) {
        state.phus = phus
        applyProductWorkstations(state)
    },
    setConfigurationData(state, data) {
        const options = data.options || {}
        state.layouts = normalizeLayouts(options.layouts)
        state.minSlotsPerProduct = Number(options.min_slots_per_product) || MIN_SLOTS_PER_PRODUCT
        state.workstationServerIds = (data.workstations || []).reduce((acc, workstation) => {
            acc[workstation.type] = workstation.id
            return acc
        }, {})
        state.phus = normalizeConfigPhus(data.workstations, state.layouts)
        applyProductWorkstations(state)
        state.equipmentCatalog = normalizeEquipmentCatalog(options.equipment)
        state.fixedLayout = options.fixed_layout === true || options.fixed_layout === 1 || options.fixed_layout === '1' || options.fixed_layout === 'true'
        state.fifoSales = options.fifo_sales === true || options.fifo_sales === 1 || options.fifo_sales === '1' || options.fifo_sales === 'true'
        state.fryerEquipment = data.equipment && data.equipment.fryer ? data.equipment.fryer : []
        state.broilerEquipment = normalizeBroilers(data.equipment ? data.equipment.broiler : [])
    },
    setPhuEditing(state, { phuId, editing }) {
        const others = state.editingPhuIds.filter((id) => id !== phuId)
        state.editingPhuIds = editing ? [...others, phuId] : others
    },
    mergeHoldingUnits(state, { data, skipPhuIds }) {
        const incoming = normalizeConfigPhus(data.workstations, state.layouts)
        const byId = {}
        incoming.forEach((phu) => {
            phu.slots.forEach((slot) => {
                byId[slot.id] = slot
            })
        })
        state.phus.forEach((phu) => {
            if (skipPhuIds.includes(phu.id)) {
                return
            }
            phu.slots.forEach((slot) => {
                const next = byId[slot.id]
                if (!next || String(slot.batch_id || '').indexOf('temp_') === 0) {
                    return
                }
                slot.product_id = next.product_id
                writeRuntime(slot, readRuntime(next))
            })
        })
        applyProductWorkstations(state)
    },
    setFryerEquipment(state, equipment) {
        state.fryerEquipment = equipment
    },
    setBroilerEquipment(state, equipment) {
        state.broilerEquipment = equipment
    },
    setForecastReady(state, value) {
        state.forecastReady = value
    },
    setForecast(state, { recommendations, cookingBatches, stock, summary }) {
        state.recommendations = recommendations
        state.cookingBatches = cookingBatches
        state.stock = stock
        state.summary = summary
    },
    addCookingBatch(state, batch) {
        state.cookingBatches = [...state.cookingBatches, batch]
    },
    removeCookingBatch(state, batchId) {
        state.cookingBatches = state.cookingBatches.filter((batch) => batch.batch_id !== batchId)
    },
    replaceCookingBatchId(state, { temporalBatchId, batchId }) {
        const batch = state.cookingBatches.find((item) => item.batch_id === temporalBatchId)
        if (batch) {
            batch.batch_id = batchId
        }
    },
    removeRecommendation(state, recommendationId) {
        state.recommendations = state.recommendations.filter((item) => item.id !== recommendationId)
    },
    assignBatchToSlot(state, { batch, slotId }) {
        const slot = findSlot(state.phus, slotId)
        const product = state.products[batch.product_id]

        slot.units = batch.units
        slot.batch_id = batch.batch_id
        slot.started_at = batch.started_at
        slot.ready_at = batch.ends_at
        slot.expires_at = batch.ends_at + product.holding_time * MINUTE_MS
        state.assignedBatchIds = [...state.assignedBatchIds, batch.batch_id]
    },
    setSlotBatch(state, { slotId, batchId }) {
        const slot = findSlot(state.phus, slotId)
        if (!slot) {
            return
        }
        slot.batch_id = batchId
    },
    setPhuSlots(state, { phuId, slots }) {
        const phu = state.phus.find((item) => item.id === phuId)
        phu.slots = slots
    },
    clearSlot(state, slotId) {
        clearSlot(findSlot(state.phus, slotId))
    },
    reduceSlotUnits(state, { slotId, units }) {
        const slot = findSlot(state.phus, slotId)
        if (!slot) {
            return
        }
        slot.units = Math.max(0, Number(slot.units) - units)
    },
    restoreSlot(state, previous) {
        const slot = findSlot(state.phus, previous.slotId)
        if (!slot) {
            return
        }
        slot.units = previous.units
        slot.batch_id = previous.batch_id
        slot.started_at = previous.started_at
        slot.ready_at = previous.ready_at
        slot.expires_at = previous.expires_at
    },
    applyServerSlots(state, slots) {
        const updates = slots || []
        updates.forEach((formatted) => {
            const slot = findSlot(state.phus, formatted.id) ||
                findSlot(state.phus, String(formatted.id)) ||
                findSlot(state.phus, Number(formatted.id))
            if (!slot) {
                return
            }
            slot.units = Number(formatted.current_units) || 0
            slot.batch_id = formatted.batch_id
            slot.started_at = toTimestampOrNull(formatted.created_date)
            slot.ready_at = toTimestampOrNull(formatted.ready_date)
            slot.expires_at = toTimestampOrNull(formatted.expiration_date)
        })
    },
    registerStockWaste(state, { productId, units }) {
        const stockItem = state.stock.find((item) => item.product_id === productId)
        if (!stockItem) {
            return
        }
        stockItem.units -= units
        stockItem.waste += units
    },
    restoreStockWaste(state, { productId, units }) {
        const stockItem = state.stock.find((item) => item.product_id === productId)
        if (!stockItem) {
            return
        }
        stockItem.units += units
        stockItem.waste = Math.max(0, stockItem.waste - units)
    },
    resetState(state) {
        Object.assign(state, getDefaultState())
    }
}

const syncRecommendation = (dispatch, recommendation, product, units) => {
    return dispatch('production/editBatch', {
        batchId: recommendation.batch_id,
        reference_id: product.id,
        quantity: units,
        date: moment().format(DATETIME_FORMAT),
        duration: product.cooking_time,
        business_date: businessDate()
    }, { root: true }).then((response) => {
        const remaining = recommendation.units - units
        if (isFailedResponse(response) || remaining <= 0) {
            return response
        }
        return dispatch('production/addBatch', {
            reference_id: product.id,
            quantity: remaining,
            recommended_date: recommendation.recommended_date,
            business_date: businessDate()
        }, { root: true }).then(() => response)
    })
}

const addManualBatch = (dispatch, product, units, readyDate) => {
    const now = moment().format(DATETIME_FORMAT)
    return dispatch('production/addBatch', {
        reference_id: product.id,
        quantity: units,
        business_date: businessDate(),
        recommended_date: now,
        ready_date: readyDate,
        created_date: now,
        data: JSON.stringify({ user_created: 1 }),
        status: 1,
        offline: 0
    }, { root: true })
}

const actions = {
    loadData({ commit, dispatch, rootGetters }) {
        return Promise.all([
            dispatch('production/loadData', null, { root: true }),
            dispatch('loadConfiguration')
        ]).then(() => {
            commit('setProducts', normalizeProducts(rootGetters['production/getProducts']))
            return dispatch('loadForecast')
        })
    },
    demoSale({ dispatch }, { productId, units }) {
        return whileHoldingWrite(() => api.put(DEMO_SALE_URL, { reference_id: productId, units }).then((response) => {
            if (isFailedResponse(response)) {
                throw new Error('sync_error')
            }
            return dispatch('loadConfiguration')
        }))
    },
    loadConfiguration({ commit }) {
        holdingGeneration += 1
        return api.get(`${CONFIGURATION_URL}?t=${Date.now()}`).then((response) => {
            if (isFailedResponse(response)) {
                throw new Error('sync_error')
            }
            holdingGeneration += 1
            commit('setConfigurationData', response.data)
        })
    },
    refreshHoldingUnits({ state, commit }) {
        if (holdingWrites > 0) {
            return Promise.resolve()
        }
        const generation = holdingGeneration
        return api.get(`${CONFIGURATION_URL}?t=${Date.now()}`).then((response) => {
            if (isFailedResponse(response) || isHoldingStale(generation)) {
                return
            }
            commit('mergeHoldingUnits', { data: response.data, skipPhuIds: state.editingPhuIds })
        })
    },
    saveConfiguration({ commit }, payload) {
        return whileHoldingWrite(() => putConfiguration(payload).then((data) => {
            holdingGeneration += 1
            commit('setConfigurationData', data)
            return data
        }))
    },
    loadForecast({ state, commit, dispatch, rootGetters }, options = {}) {
        if (options.background && holdingWrites > 0) {
            return Promise.resolve()
        }
        const generation = holdingGeneration
        return dispatch('production/loadForecast', { date: businessDate(), type: FORECAST_TYPE }, { root: true }).then(() => {
            if (options.background && isHoldingStale(generation)) {
                return
            }
            const forecast = rootGetters['production/getForecast'] || {}
            const forecastProducts = forecast.products || {}

            commit('setForecastReady', Boolean(forecastProducts.pls))

            commit('setForecast', {
                recommendations: normalizeRecommendations(forecastProducts.pls, state.products),
                cookingBatches: normalizeCookingBatches(forecastProducts.cooking, state.products),
                stock: normalizeStock(forecastProducts.stored, forecast.phu_stock, state.products),
                summary: forecast.summary || {}
            })
        })
    },
    applyRecommendation({ state, getters, commit, dispatch }, { recommendation, units, slotId }) {
        const product = state.products[recommendation.product_id]
        const slot = findSlot(state.phus, slotId)
        const phu = findPhuOfSlot(state.phus, slotId)

        if (!product || !getters.hasAvailableEquipment(product.workstation_id, Date.now())) {
            return Promise.reject(new Error(BLOCKED_REASONS.NO_COOKING_CAPACITY))
        }
        if (!canCookInSlot(slot, product) || !phu) {
            return Promise.reject(new Error(BLOCKED_REASONS.NO_PHU_CAPACITY))
        }
        const target = { phuId: phu.id, slotId: slot.id }

        return whileHoldingWrite(() => {
        const startedAt = moment()
        const readyDate = startedAt.clone().add(product.cooking_time, 'minutes')
        const temporalBatchId = `temp_${Date.now()}`
        let batchId = temporalBatchId
        const batch = {
            batch_id: temporalBatchId,
            product_id: product.id,
            units,
            started_at: startedAt.valueOf(),
            ends_at: readyDate.valueOf()
        }

        commit('addCookingBatch', batch)
        commit('assignBatchToSlot', { batch, slotId: target.slotId })
        if (recommendation.id) {
            commit('removeRecommendation', recommendation.id)
        }

        const request = recommendation.batch_id
            ? syncRecommendation(dispatch, recommendation, product, units)
            : addManualBatch(dispatch, product, units, readyDate.format(DATETIME_FORMAT))

        return request
            .then((response) => {
                if (isFailedResponse(response)) {
                    throw new Error('sync_error')
                }
                batchId = (response.data && response.data.id) || recommendation.batch_id
                if (!batchId) {
                    throw new Error('sync_error')
                }
                if (response.data && response.data.id) {
                    commit('replaceCookingBatchId', { temporalBatchId, batchId })
                }
                commit('setSlotBatch', { slotId: target.slotId, batchId })
                return api.put(`pls/phu/${target.phuId}/slots/${target.slotId}/cook`, {
                    batch_id: batchId,
                    holding_minutes: product.holding_time
                })
            })
            .then((response) => {
                if (isFailedResponse(response)) {
                    throw new Error('sync_error')
                }
                return dispatch('loadForecast').catch(() => undefined)
            })
            .catch((error) => {
                commit('removeCookingBatch', batchId)
                commit('clearSlot', target.slotId)
                dispatch('loadForecast')
                throw error
            })
        })
    },
    toggleEquipment({ dispatch }, equipment) {
        if (equipment.broiler) {
            return dispatch('toggleBroiler', equipment.id)
        }

        return dispatch('toggleFryer', equipment.id)
    },
    toggleFryer({ state, commit }, equipmentId) {
        const previous = state.fryerEquipment
        const target = previous.find((item) => item.id === equipmentId)
        if (!target || target.status === EQUIPMENT_STATUS.COOKING) {
            return Promise.resolve(false)
        }
        const next = previous.map((item) => {
            if (item.id !== equipmentId) {
                return item
            }
            const status = item.status === EQUIPMENT_STATUS.UNAVAILABLE ? EQUIPMENT_STATUS.AVAILABLE : EQUIPMENT_STATUS.UNAVAILABLE
            return { ...item, status }
        })

        const generation = ++equipmentGeneration.fryer
        commit('setFryerEquipment', next)
        return saveEquipment('fryer', generation, () => ({
            equipment: {
                fryer: state.fryerEquipment.map((item) => ({ id: item.id, status: item.status }))
            }
        })).catch((error) => {
            if (generation === equipmentGeneration.fryer) {
                commit('setFryerEquipment', previous)
            }
            throw error
        })
    },
    toggleBroiler({ state, commit }, equipmentId) {
        const previous = state.broilerEquipment
        const target = previous.find((item) => item.id === equipmentId)
        if (!target || target.status === EQUIPMENT_STATUS.COOKING) {
            return Promise.resolve(false)
        }
        const next = previous.map((item) => {
            if (item.id !== equipmentId) {
                return item
            }
            const status = item.status === EQUIPMENT_STATUS.UNAVAILABLE ? EQUIPMENT_STATUS.AVAILABLE : EQUIPMENT_STATUS.UNAVAILABLE
            return { ...item, status }
        })

        const generation = ++equipmentGeneration.broiler
        commit('setBroilerEquipment', next)
        return saveEquipment('broiler', generation, () => ({
            equipment: { broiler: toBroilerPayload(state.broilerEquipment) }
        })).catch((error) => {
            if (generation === equipmentGeneration.broiler) {
                commit('setBroilerEquipment', previous)
            }
            throw error
        })
    },
    assignCookingToSlot({ commit }, params) {
        return whileHoldingWrite(() => {
            commit('assignBatchToSlot', params)
            return true
        })
    },
    transferSlotBetweenPhus({ state, commit }, { source, targetSlotId }) {
        const persisted = findPersistedSlot(state.phus, source)
        const target = findSlot(state.phus, targetSlotId) || findSlot(state.phus, String(targetSlotId)) || findSlot(state.phus, Number(targetSlotId))
        if (!persisted || !target || persisted.id === target.id || String(persisted.product_id) !== String(target.product_id)) {
            return Promise.reject(new Error('sync_error'))
        }
        const sourcePhu = findPhuOfSlot(state.phus, persisted.id)
        const targetPhu = findPhuOfSlot(state.phus, target.id)
        if (!sourcePhu || !targetPhu) {
            return Promise.reject(new Error('sync_error'))
        }
        return whileHoldingWrite(() => {
        const previousSource = cloneDeep(sourcePhu.slots)
        const previousTarget = cloneDeep(targetPhu.slots)
        const nextSource = cloneDeep(sourcePhu.slots)
        const nextTarget = sourcePhu.id === targetPhu.id ? nextSource : cloneDeep(targetPhu.slots)
        const sourceSlot = nextSource.find((slot) => slot.id === persisted.id)
        const targetSlot = nextTarget.find((slot) => slot.id === target.id)
        const sourceRuntime = readRuntime(sourceSlot)
        writeRuntime(sourceSlot, readRuntime(targetSlot))
        writeRuntime(targetSlot, sourceRuntime)
        commit('setPhuSlots', { phuId: sourcePhu.id, slots: nextSource })
        if (sourcePhu.id !== targetPhu.id) {
            commit('setPhuSlots', { phuId: targetPhu.id, slots: nextTarget })
        }

        return api.put('pls/phu/slots/transfer', {
            source_slot_id: persisted.id,
            target_slot_id: target.id
        }).then((response) => {
            if (isFailedResponse(response)) {
                throw new Error('sync_error')
            }
            return true
        }).catch((error) => {
            commit('setPhuSlots', { phuId: sourcePhu.id, slots: previousSource })
            if (sourcePhu.id !== targetPhu.id) {
                commit('setPhuSlots', { phuId: targetPhu.id, slots: previousTarget })
            }
            throw error
        })
        })
    },
    saveSlotPositions({ state, commit }, params) {
        const phu = state.phus.find((item) => item.id === params.phuId)
        return whileHoldingWrite(() => {
            const previous = phu ? cloneDeep(phu.slots) : []
            commit('setPhuSlots', params)

            return api.put(`pls/phu/${params.phuId}/slots`, {
                slots: params.slots.map((slot) => ({
                    id: slot.id,
                    position: slot.position,
                    batch_id: slot.batch_id || null
                }))
            }).then((response) => {
                if (isFailedResponse(response)) {
                    throw new Error('sync_error')
                }
                return true
            }).catch((error) => {
                commit('setPhuSlots', { phuId: params.phuId, slots: previous })
                throw error
            })
        })
    },
    registerSlotWaste({ state, commit }, { slot, units: requested }) {
        const current = resolveWasteSlot(state.phus, slot)
        if (!current || resolveSlotState(current, Date.now()) !== SLOT_STATES.EXPIRED) {
            return Promise.resolve(false)
        }
        const product = state.products[slot.product_id]
        const phu = findPhuOfSlot(state.phus, current.id)
        const available = Number(current.units) || 0
        const units = Math.min(Number(requested) || available, available)
        if (!product || !phu || units <= 0) {
            return Promise.resolve(false)
        }

        return whileHoldingWrite(() => {
        const previous = {
            slotId: current.id,
            units: current.units,
            batch_id: current.batch_id,
            started_at: current.started_at,
            ready_at: current.ready_at,
            expires_at: current.expires_at
        }
        if (units < available) {
            commit('reduceSlotUnits', { slotId: current.id, units })
        } else {
            commit('clearSlot', current.id)
        }
        commit('registerStockWaste', { productId: slot.product_id, units })

        return api.put(`pls/phu/${phu.id}/slots/${current.id}/waste`, {
            business_date: businessDate(),
            units
        }).then((response) => {
            if (isFailedResponse(response)) {
                throw new Error(response && response.msg ? response.msg : 'sync_error')
            }
            return {
                phuId: phu.id,
                slotId: current.id,
                productId: slot.product_id,
                units,
                previous
            }
        }).catch((error) => {
            commit('restoreSlot', previous)
            commit('restoreStockWaste', { productId: slot.product_id, units })
            throw error
        })
        })
    },
    registerStockWaste({ state, commit }, { productId, units }) {
        const product = state.products[productId]
        if (!product || units <= 0) {
            return Promise.resolve(false)
        }

        const onHand = visibleStockUnits(state.stock, state.cookingBatches, productId, Date.now())
        if (onHand > 0 && units > onHand) {
            return Promise.resolve(false)
        }

        return whileHoldingWrite(() => {
            const plan = planReadySlotWaste(state.phus, productId, units, Date.now())
            commit('registerStockWaste', { productId, units })
            applyWastePlan(commit, plan)

            return api.put('pls/phu/waste', {
                reference_id: product.id,
                units,
                business_date: businessDate()
            }).then((response) => {
                if (isFailedResponse(response)) {
                    throw new Error(response && response.msg ? response.msg : 'sync_error')
                }
                commit('applyServerSlots', response.data || [])
                return { productId, units, plan }
            }).catch((error) => {
                plan.forEach((step) => commit('restoreSlot', step.previous))
                commit('restoreStockWaste', { productId, units })
                throw error
            })
        })
    },
    undoSlotWaste({ commit }, waste) {
        commit('restoreSlot', waste.previous)
        commit('restoreStockWaste', { productId: waste.productId, units: waste.units })

        return whileHoldingWrite(() => api.put(`pls/phu/${waste.phuId}/slots/${waste.slotId}/waste/undo`, {
            business_date: businessDate(),
            units: waste.units
        }).then((response) => {
            if (isFailedResponse(response)) {
                throw new Error(response && response.msg ? response.msg : 'sync_error')
            }
            return true
        }).catch((error) => {
            if (waste.units < Number(waste.previous.units)) {
                commit('reduceSlotUnits', { slotId: waste.slotId, units: waste.units })
            } else {
                commit('clearSlot', waste.slotId)
            }
            commit('registerStockWaste', { productId: waste.productId, units: waste.units })
            throw error
        }))
    },
    undoStockWaste({ state, commit }, { productId, units, plan }) {
        const product = state.products[productId]
        if (!product) {
            return Promise.resolve(false)
        }
        const steps = Array.isArray(plan) ? plan : []
        commit('restoreStockWaste', { productId, units })
        steps.forEach((step) => commit('restoreSlot', step.previous))

        return whileHoldingWrite(() => api.put('pls/phu/waste/undo', {
            reference_id: product.id,
            units,
            business_date: businessDate()
        }).then((response) => {
            if (isFailedResponse(response)) {
                throw new Error(response && response.msg ? response.msg : 'sync_error')
            }
            commit('applyServerSlots', response.data || [])
            return true
        }).catch((error) => {
            applyWastePlan(commit, steps)
            commit('registerStockWaste', { productId, units })
            throw error
        }))
    }
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
}
